import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { RouteDirectionEnum } from '../../../../generated/graphql';
import {
  selectHasChangesInProgress,
  selectMapRouteEditor,
  setRouteMetadataFormOpenAction,
  useAppDispatch,
  useAppSelector,
} from '../../../../redux';
import { Priority } from '../../../../types/enums';
import {
  filterDistinctConsecutiveStops,
  filterHighestPriorityCurrentStops,
  getTransportModeIcon,
} from '../../../../utils';
import { mapVehicleModeToRouteColor } from '../../../../utils/colors';
import { mapDirectionToSymbol } from '../../../../utils/i18n';
import { EditButton } from '../../../common/Buttons';
import { Row, Visible } from '../../../common/LayoutComponents';
import { RouteLabel } from '../../../LinesAndRoutes/Common';
import { CustomOverlay } from '../../CustomOverlay';
import { MapOverlay, MapOverlayHeader } from '../../MapOverlay';
import { PriorityBadge } from '../../PriorityBadge';
import { useMapObservationDate } from '../../Utils/mapUrlState';
import { RouteStopsOverlayRow } from './RouteStopsOverlayRow';
import { useRouteInfo } from './useRouteInfo';

const testIds = {
  mapOverlayHeader: 'RouteStopsOverlay::mapOverlayHeader',
  routeStopListHeader: (label: string, direction: RouteDirectionEnum) =>
    `RouteStopsOverlay::routeStopListHeader::${label}-${direction}`,
};

type RouteStopsOverlayProps = {
  readonly className?: string;
};

export const RouteStopsOverlay: FC<RouteStopsOverlayProps> = ({
  className,
}) => {
  const dispatch = useAppDispatch();
  const routeEditingInProgress = useAppSelector(selectHasChangesInProgress);
  const observationDate = useMapObservationDate();
  const { selectedRouteId, creatingNewRoute } =
    useAppSelector(selectMapRouteEditor);

  const { t } = useTranslation();

  const {
    routeMetadata,
    routeVehicleMode,
    routeLineType,
    includedStopLabels,
    stopsEligibleForJourneyPattern,
    // selectedRouteId is undefined if we are creating a new route
  } = useRouteInfo(selectedRouteId);

  if (!routeMetadata || !routeVehicleMode) {
    return null;
  }

  const highestPriorityCurrentStops = filterHighestPriorityCurrentStops(
    stopsEligibleForJourneyPattern,
    observationDate,
    routeMetadata.priority === Priority.Draft,
  );
  const highestPriorityStopsEligibleForJourneyPattern =
    filterDistinctConsecutiveStops(highestPriorityCurrentStops);

  /**
   * When editing a route, we want to show all stops eligible for route,
   * When displaying, we don't want to show stops that are not in the journey pattern
   */
  const stopsToShow = routeEditingInProgress
    ? highestPriorityStopsEligibleForJourneyPattern
    : highestPriorityStopsEligibleForJourneyPattern.filter((stop) =>
        includedStopLabels.includes(stop.label),
      );
  const routeName = routeMetadata?.name_i18n.fi_FI;

  return (
    <CustomOverlay position="top-left">
      <MapOverlay className={className}>
        <MapOverlayHeader testId={testIds.mapOverlayHeader}>
          <i
            className={twJoin(
              getTransportModeIcon(routeVehicleMode, routeLineType),
              'mt-1 text-xl',
            )}
          />

          <div>
            <p className="text-base text-tweaked-brand">
              <RouteLabel
                label={routeMetadata.label}
                variant={routeMetadata.variant}
              />
            </p>
            <p className="text-light text-xs text-gray-500">{routeName}</p>
          </div>
          <Visible visible={creatingNewRoute}>
            <EditButton
              onClick={() => dispatch(setRouteMetadataFormOpenAction(true))}
              tooltip={t(($) => $.accessibility.map.editRoute, {
                routeName,
              })}
            />
          </Visible>
        </MapOverlayHeader>
        <div className="flex items-start border-b px-3 py-2">
          <div
            className="mt-1 ml-1 flex h-6 w-6 items-center justify-center rounded-xs font-bold text-white"
            style={{
              backgroundColor: mapVehicleModeToRouteColor(
                routeVehicleMode,
                routeLineType,
              ),
            }}
          >
            {mapDirectionToSymbol(t, routeMetadata.direction)}
          </div>
          <div
            data-testid={testIds.routeStopListHeader(
              routeMetadata.label,
              routeMetadata.direction,
            )}
            className="ml-2 flex flex-col"
          >
            <Row className="items-center gap-2">
              <p className="text-base text-black">
                <RouteLabel
                  label={routeMetadata.label}
                  variant={routeMetadata.variant}
                />
              </p>
              <PriorityBadge
                priority={routeMetadata.priority}
                validityStart={routeMetadata.validity_start}
                validityEnd={routeMetadata.validity_end}
              />
            </Row>
            <div className="text-light text-xs text-gray-500">
              {routeMetadata.name_i18n.fi_FI}
            </div>
          </div>
        </div>
        <div className="overflow-y-auto">
          {stopsToShow?.map((stop) => (
            <RouteStopsOverlayRow
              key={stop.scheduled_stop_point_id}
              stop={stop}
              isReadOnly={!routeEditingInProgress}
              belongsToJourneyPattern={includedStopLabels.includes(stop.label)}
            />
          ))}
        </div>
      </MapOverlay>
    </CustomOverlay>
  );
};
